import { useState } from "react";
import { Loader2, Copy, ExternalLink, PhoneCall, Trophy, XCircle, Radar, ChevronDown, ChevronUp, Snowflake } from "lucide-react";
import { timeAgo, priceLabel } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PLATFORM_LABEL = {
  dealer_com: "Dealer.com", ridemotive: "RideMotive", crawl: "Other · page crawl",
  unknown: "No feed found", demo: "Demo store",
};
const SOURCE_LABEL = { invite: "branded link", tour: "found the tour", contact: "contact form", signup: "signed up" };

export const ProspectCard = ({ p, busy, onPatch, onLink, onDetect, onCopy }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState(p.website || "");
  const working = busy === p.key;

  return (
    <div data-testid={`prospect-${p.key}`}
         className={`rounded-lg border bg-[#0B0D14] p-3 ${p.cold ? "border-amber-700/60 ring-1 ring-amber-700/30" : "border-[#1C2233]"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" title={p.dealer_name}>{p.dealer_name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-600 font-mono-tech mt-0.5">
            {p.where || SOURCE_LABEL[p.source] || p.source}{p.cold ? " · going cold" : ""}
          </div>
        </div>
        {p.cold && <Snowflake className="h-3.5 w-3.5 text-amber-400 shrink-0" title={`No activity for over 2 days`} />}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2">
        <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px] font-mono-tech">{SOURCE_LABEL[p.source] || p.source}</Badge>
        {p.inventory_platform && (
          <Badge variant="outline" title={p.inventory_platform_detail} data-testid={`prospect-platform-${p.key}`}
                 className={`text-[10px] font-mono-tech ${["dealer_com", "ridemotive"].includes(p.inventory_platform) ? "border-emerald-700/50 text-emerald-400" : "border-amber-700/50 text-amber-400"}`}>
            {PLATFORM_LABEL[p.inventory_platform] || p.inventory_platform}
          </Badge>
        )}
        {p.opens > 0 && <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px] font-mono-tech">{p.opens} open{p.opens === 1 ? "" : "s"}</Badge>}
        {p.leads > 0 && <Badge variant="outline" className="border-[#0A8CF2]/50 text-[#7FC3FF] text-[10px] font-mono-tech">{p.leads} lead{p.leads === 1 ? "" : "s"}</Badge>}
      </div>

      {(p.contact_name || p.contact_phone || p.contact_email) && (
        <div className="mt-2 text-xs text-slate-300" data-testid={`prospect-contact-${p.key}`}>
          {p.contact_name}{p.contact_phone ? ` · ${p.contact_phone}` : ""}{!p.contact_phone && p.contact_email ? ` · ${p.contact_email}` : ""}
        </div>
      )}
      {p.car && <div className="text-[11px] text-slate-500 mt-0.5">{p.car}{p.unlock_price ? ` · ${priceLabel(p.unlock_price)} shown` : ""}</div>}

      <div className="text-[10px] text-slate-600 font-mono-tech mt-2">
        {p.last_activity_at ? `last activity ${timeAgo(p.last_activity_at)}` : "no activity yet"}
        {p.next_action_at ? ` · follow up ${p.next_action_at}` : ""}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 mt-2.5">
        {p.token ? (
          <>
            <button type="button" onClick={() => onCopy(p.url)} data-testid={`prospect-copy-${p.key}`} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"><Copy className="h-3 w-3" /> link</button>
            <a href={`/try/${p.token}`} target="_blank" rel="noreferrer" data-testid={`prospect-open-${p.key}`} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1">tour <ExternalLink className="h-3 w-3" /></a>
          </>
        ) : (
          <button type="button" onClick={() => onLink(p)} disabled={working} data-testid={`prospect-link-${p.key}`} className="text-[11px] text-[#7FC3FF] hover:text-white inline-flex items-center gap-1 disabled:opacity-50">make a branded link</button>
        )}
        {p.status !== "won" && !p.contacted_at && (
          <button type="button" onClick={() => onPatch(p, { status: "contacted" })} disabled={working} data-testid={`prospect-contacted-${p.key}`} className="text-[11px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 disabled:opacity-50"><PhoneCall className="h-3 w-3" /> called</button>
        )}
        {p.status !== "won" && (
          <button type="button" onClick={() => onPatch(p, { status: "won" })} disabled={working} data-testid={`prospect-won-${p.key}`} className="text-[11px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 disabled:opacity-50"><Trophy className="h-3 w-3" /> won</button>
        )}
        {p.status !== "lost" && (
          <button type="button" onClick={() => onPatch(p, { status: "lost" })} disabled={working} data-testid={`prospect-lost-${p.key}`} className="text-[11px] text-slate-500 hover:text-red-300 inline-flex items-center gap-1 disabled:opacity-50"><XCircle className="h-3 w-3" /> passed</button>
        )}
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`prospect-expand-${p.key}`} className="ml-auto text-slate-500 hover:text-white">
          {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-[#1C2233] space-y-2.5" data-testid={`prospect-detail-${p.key}`}>
          <div className="flex gap-2">
            <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="their website"
                   data-testid={`prospect-website-${p.key}`} className="h-8 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 text-xs" />
            <Button size="sm" variant="outline" disabled={working} data-testid={`prospect-detect-${p.key}`}
                    onClick={() => (website !== p.website ? onPatch(p, { website }).then(() => onDetect({ ...p, website })) : onDetect(p))}
                    className="h-8 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 text-xs shrink-0">
              <Radar className="mr-1 h-3 w-3" /> platform
            </Button>
          </div>
          <Input type="date" value={p.next_action_at || ""} onChange={(e) => onPatch(p, { next_action_at: e.target.value })}
                 data-testid={`prospect-followup-${p.key}`} className="h-8 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 text-xs" />
          <div className="flex gap-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="add a note (what they said)"
                   data-testid={`prospect-note-${p.key}`} className="h-8 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 text-xs" />
            <Button size="sm" disabled={working || !note.trim()} data-testid={`prospect-note-save-${p.key}`}
                    onClick={() => onPatch(p, { note }).then(() => setNote(""))} className="h-8 bg-[#0A8CF2] hover:bg-[#3AA3FF] text-xs shrink-0">save</Button>
          </div>
          {(p.notes || []).slice().reverse().map((n, i) => (
            <div key={i} className="text-[11px] text-slate-400 border-l-2 border-[#1C2233] pl-2">
              {n.text}
              <span className="block text-[10px] text-slate-600 font-mono-tech">{n.by} · {timeAgo(n.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
